import {
  Brush as BrushIcon,
  Dashboard as CanvasIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Launch as LaunchIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { useState, type FC } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router';
import ConfirmPopover from 'src/components/confirm-popover';
import { usePermissionGuard } from 'src/guards/organization-guard';
import $log from 'src/log/log';
import { paths } from 'src/paths';
import { useDispatch } from 'src/store';
import { thunks } from 'src/thunks/infinite-canvas';
import { InfiniteCanvasDto } from 'src/types/infinite-canvas-dto';
import { useInfiniteCanvases } from '../../../../hooks/use-infinite-canvases';
import { Canvas } from '../../../../types/canvas';

const CanvasTableRow: FC<{
  canvas: InfiniteCanvasDto;
  onOpen: (canvasId: string) => void;
  onDelete: (canvasId: string) => void;
  onRename: (canvasId: string, newName: string) => void;
}> = ({ canvas, onOpen, onDelete, onRename }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpen = () => {
    onOpen(canvas.id);
    handleMenuClose();
  };

  const handleRowClick = () => {
    onOpen(canvas.id);
  };

  const handleRenameClick = () => {
    setNewName(canvas.name || '');
    setRenameDialogOpen(true);
    handleMenuClose();
  };

  const handleRenameCancel = () => {
    setRenameDialogOpen(false);
    setNewName('');
  };

  const handleRenameConfirm = () => {
    if (newName.trim() && newName.trim() !== canvas.name) {
      onRename(canvas.id, newName.trim());
    }
    setRenameDialogOpen(false);
    setNewName('');
  };

  const handleRenameKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleRenameConfirm();
    } else if (event.key === 'Escape') {
      handleRenameCancel();
    }
  };

  return (
    <>
      <TableRow
        onClick={handleRowClick}
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: '#f8fafc',
            transform: 'scale(1.01)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            '& .MuiTableCell-root': {
              borderColor: 'primary.main',
            },
          },
          '& .MuiTableCell-root': {
            borderBottom: '1px solid #e8e8e8',
            py: 2,
          },
        }}
      >
        {/* Canvas Icon & Name */}
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ width: 40, height: 40 }}>
              <BrushIcon fontSize="small" />
            </Avatar>
            <Stack>
              <Typography variant="body1" fontWeight="bold">
                {canvas.name || 'Untitled Canvas'}
              </Typography>
              <Chip
                size="small"
                label="Canvas"
                sx={{
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  fontWeight: 'medium',
                  fontSize: '0.7rem',
                  height: 20,
                  width: 'fit-content',
                }}
              />
            </Stack>
          </Stack>
        </TableCell>

        {/* Created Date */}
        <TableCell>
          <Stack>
            <Typography variant="body2">{moment(canvas.createdAt).format('MMM DD, YYYY')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {moment(canvas.createdAt).format('h:mm A')}
            </Typography>
          </Stack>
        </TableCell>

        {/* Last Modified */}
        <TableCell>
          <Stack>
            <Typography variant="body2">
              {canvas.updatedAt
                ? moment(canvas.updatedAt).format('MMM DD, YYYY')
                : moment(canvas.createdAt).format('MMM DD, YYYY')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {canvas.updatedAt ? moment(canvas.updatedAt).fromNow() : moment(canvas.createdAt).fromNow()}
            </Typography>
          </Stack>
        </TableCell>

        {/* Actions */}
        <TableCell>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {/* <Button
              variant="outlined"
              size="small"
              startIcon={<LaunchIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onOpen(canvas.id);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 'medium',
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  borderColor: 'primary.main',
                },
              }}
            >
              Open
            </Button> */}
            <IconButton
              size="small"
              onClick={handleMenuClick}
              sx={{
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                color: 'text.secondary',
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
          },
        }}
      >
        <MenuItem onClick={handleOpen}>
          <ListItemIcon>
            <LaunchIcon fontSize="small" />
          </ListItemIcon>
          Open Canvas
        </MenuItem>
        <MenuItem onClick={handleRenameClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Rename
        </MenuItem>
        <ConfirmPopover
          content={
            <Typography>
              Are you sure you want to delete <b>{canvas.name}</b>?
              <br />
              <b>This action cannot be undone.</b>
            </Typography>
          }
          onConfirm={() => {
            onDelete(canvas.id);
            handleMenuClose();
          }}
          title=""
          actionLabels={{
            confirmButtonLabel: 'Delete',
          }}
          actionProps={{
            confirmButtonProps: {
              color: 'error',
            },
          }}
          onCancel={handleMenuClose}
          component={(handleClick) => (
            <MenuItem onClick={handleClick} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              Delete Canvas
            </MenuItem>
          )}
        />
      </Menu>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={handleRenameCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Rename Canvas</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Canvas Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={handleRenameKeyPress}
            placeholder="Enter canvas name"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleRenameConfirm}
            variant="contained"
            disabled={!newName.trim() || newName.trim() === canvas.name}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const TableSkeleton: FC = () => (
  <TableContainer>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>
            <Skeleton variant="text" width={120} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={100} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={100} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={80} />
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[1, 2, 3, 4].map((i) => (
          <TableRow key={i}>
            <TableCell>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Skeleton variant="circular" width={40} height={40} />
                <Stack spacing={1}>
                  <Skeleton variant="text" width={150} height={20} />
                  <Skeleton variant="rectangular" width={60} height={16} sx={{ borderRadius: 1 }} />
                </Stack>
              </Stack>
            </TableCell>
            <TableCell>
              <Stack spacing={1}>
                <Skeleton variant="text" width={100} height={16} />
                <Skeleton variant="text" width={80} height={12} />
              </Stack>
            </TableCell>
            <TableCell>
              <Stack spacing={1}>
                <Skeleton variant="text" width={100} height={16} />
                <Skeleton variant="text" width={80} height={12} />
              </Stack>
            </TableCell>
            <TableCell>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Skeleton variant="rectangular" width={70} height={32} sx={{ borderRadius: 1 }} />
                <Skeleton variant="circular" width={32} height={32} />
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export const ListCanvas: FC<{
  canvases: Canvas[];
}> = ({ canvases }) => {
  const { teamId } = useParams();
  const permissions = usePermissionGuard({
    permissions: ['admin', 'editor', 'organization', 'viewer'],
  });
  const { iCanvases, getCanvases: getInfiniteCanvases, isLoading } = useInfiniteCanvases(teamId);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleNavigation = (canvasId: string) => {
    navigate(paths.infiniteCanvas.canvas(teamId!, canvasId));
  };

  const handleDelete = (canvasId: string) => {
    if (!teamId) {
      toast.error('Failed to delete canvas - no team');
      return;
    }
    if (!canvasId) {
      toast.error('Failed to delete canvas - no canvas');
      return;
    }
    $log.debug('deleting canvas', canvasId);
    dispatch(thunks.deleteInfiniteCanvas(teamId, canvasId));
  };

  const handleRename = (canvasId: string, newName: string) => {
    if (!teamId) {
      toast.error('Failed to rename canvas - no team');
      return;
    }
    if (!canvasId) {
      toast.error('Failed to rename canvas - no canvas');
      return;
    }
    $log.debug('renaming canvas', canvasId, 'to', newName);
    dispatch(thunks.updateInfiniteCanvas(teamId, canvasId, newName));
  };

  // Loading state
  if (isLoading) {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 3, pb: 0 }}>
            <Stack>
              <Skeleton variant="text" width={180} height={32} />
              <Skeleton variant="text" width={120} height={20} />
            </Stack>
            <Skeleton variant="rectangular" width={120} height={32} sx={{ borderRadius: 2 }} />
          </Stack>
          <TableSkeleton />
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (canvases.length === 0 && iCanvases.length === 0) {
    return (
      <Card sx={{ textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Avatar sx={{ width: 80, height: 80, margin: '0 auto', mb: 3 }}>
            <CanvasIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            No Canvases Yet
          </Typography>
          <Typography color="text.secondary" paragraph sx={{ mb: 3, fontSize: '1.1rem' }}>
            Create your first infinite canvas to start brainstorming, designing, and collaborating with your team.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<BrushIcon />}
            sx={{
              bgcolor: 'primary.main',
              textTransform: 'none',
              fontWeight: 'bold',
              borderRadius: 2,
            }}
          >
            Create Your First Canvas
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
      <CardContent sx={{ p: 0 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 3, pb: 2 }}>
          <Stack>
            <Typography variant="h5" fontWeight="bold">
              Your Canvases
            </Typography>
            <Typography color="text.secondary">
              {iCanvases.length} {iCanvases.length === 1 ? 'canvas' : 'canvases'} available
            </Typography>
          </Stack>
          <Chip
            label={`${iCanvases.length} Active`}
            color="primary"
            icon={<CanvasIcon />}
            sx={{
              px: 1,
              fontWeight: 'bold',
              '& .MuiChip-icon': { color: 'white' },
            }}
          />
        </Stack>

        {/* Table */}
        <TableContainer>
          <Table sx={{ '& .MuiTableCell-root': { border: 'none' } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 'bold', color: 'text.primary', py: 2 }}>Canvas</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'text.primary', py: 2 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'text.primary', py: 2 }}>Last Modified</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'text.primary', py: 2, textAlign: 'right' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {iCanvases.map((canvas) => (
                <CanvasTableRow
                  key={canvas.id}
                  canvas={canvas}
                  onOpen={handleNavigation}
                  onDelete={handleDelete}
                  onRename={handleRename}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};
